import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, tokenStore } from '@/lib/api';
import type { CurrentUser, TokenPair } from '@/lib/types';

export const sessionKey = ['session'] as const;

type LoginInput = { email: string; password: string; totp?: string };
type LoginResult = TokenPair & { user: CurrentUser };

/**
 * The signed-in user. Returns null rather than throwing when there is no
 * token, so the router can decide where to send someone without a try/catch.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async (): Promise<CurrentUser | null> => {
      if (!tokenStore.access && !tokenStore.refresh) return null;
      try {
        return await apiGet<CurrentUser>('/auth/me');
      } catch {
        tokenStore.clear();
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => apiPost<LoginResult>('/auth/login', input),
    onSuccess: (result) => {
      tokenStore.set(result);
      queryClient.setQueryData(sessionKey, result.user);
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      apiPost<LoginResult>('/auth/accept-invite', input),
    onSuccess: (result) => {
      tokenStore.set(result);
      queryClient.setQueryData(sessionKey, result.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = tokenStore.refresh;
      if (refreshToken) {
        // Best effort: a failed revoke must not trap someone in the app.
        await apiPost('/auth/logout', { refreshToken }).catch(() => undefined);
      }
    },
    onSettled: () => {
      tokenStore.clear();
      queryClient.clear();
      window.location.href = '/login';
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: { email: string }) =>
      apiPost<{ message: string }>('/auth/forgot-password', input),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      apiPost<void>('/auth/reset-password', input),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      apiPost<void>('/auth/change-password', input),
    onSuccess: () => {
      // Every session was revoked server-side, including this one.
      tokenStore.clear();
      window.location.href = '/login';
    },
  });
}

export function useBegin2fa() {
  return useMutation({
    mutationFn: () => apiPost<{ secret: string; qrDataUrl: string }>('/auth/2fa/setup'),
  });
}

export function useConfirm2fa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { totp: string }) =>
      apiPost<{ recoveryCodes: string[] }>('/auth/2fa/confirm', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useDisable2fa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { password: string }) => apiPost<void>('/auth/2fa/disable', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useImpersonate() {
  return useMutation({
    mutationFn: (input: { userId: string }) => apiPost<TokenPair>('/auth/impersonate', input),
    onSuccess: (pair) => {
      tokenStore.set(pair);
      window.location.href = '/dashboard';
    },
  });
}
